import * as React from "react";
import {
  AlignLeft,
  Building2,
  CalendarClock,
  ChevronRight,
  CircleSlash,
  CircleUser,
  ClipboardCheck,
  Clock,
  Hash,
  MapPin,
  Pencil,
  StickyNote,
  TriangleAlert,
  UserPlus,
} from "lucide-react";
import {
  PRIORITY_LABELS,
  type AppointmentPriority,
  type AppointmentWithContext,
} from "@taskiq/shared";
import { APPOINTMENT_PRIORITIES } from "@taskiq/shared";
import { Badge } from "@/components/ui/Badge";
import { Button } from "@/components/ui/Button";
import { useConfirm, useDiscardGuard } from "@/components/ui/confirm-context";
import { DatePicker } from "@/components/ui/DatePicker";
import { Field, Form, FormSection, Input } from "@/components/ui/Field";
import { AddressField } from "@/components/schedule/AddressField";
import { FilterSelect, type FilterOption } from "@/components/ui/FilterSelect";
import { FormMessage } from "@/components/ui/Feedback";
import { Switch } from "@/components/ui/Switch";
import { CalendarPopover } from "./CalendarPopover";
import { TextLink } from "@/components/ui/TextLink";
import { useToast } from "@/components/ui/toast-context";
import { api, apiErrorMessage } from "@/lib/api";
import { cn } from "@/lib/cn";
import {
  addDays,
  dayKeyIn,
  formatDuration,
  formatFullDay,
  formatTimeIn,
  instantFrom,
  parseTimeInput,
  parseValue,
  timeInputValue,
  toLocalDate,
  toValue,
  zonedParts,
  type CalDate,
} from "@/lib/dates";
import { useSiteUsers } from "@/lib/scope-lists";
import { useApi } from "@/lib/useApi";
import { StatusBadge } from "./StatusBadge";
import { isOverdue, type Point } from "@/lib/schedule";
import { PLACEHOLDER } from "@/lib/placeholders";

/**
 * An appointment, read then edited.
 *
 * ── Read first, edit on request ────────────────────────────────────────────
 *
 * Clicking a chip used to open the twelve-field form, which answered a question
 * nobody had asked: the click means "what is this", and nine times out of ten
 * the answer is a glance at the time, the address and who is on it. So the
 * modal opens on a read view and the form is one button away. Creating skips
 * straight to the form, because there is nothing to read yet.
 *
 * ── Hung off the chip, not centred over the page ───────────────────────────
 *
 * A drawer covered the two days you would compare against; a centred dialog
 * dimmed the whole week and threw away the one thing the click carried, which
 * is *where* on the calendar it happened. `CalendarPopover` keeps the panel
 * beside its appointment, and its own note explains what that costs.
 *
 * ── Every time on this form is the SITE's local time ───────────────────────
 *
 * The date picker and the two time inputs read and write wall-clock values in
 * the site's zone, and `instantFrom` turns them into the UTC instants the API
 * stores. Nine o'clock at a Texas yard is nine o'clock in Texas whoever is
 * typing it. The zone is printed on the form rather than assumed - see
 * docs/decisions.md #25.
 *
 * ── There is no "all day" ──────────────────────────────────────────────────
 *
 * Withdrawn from both clients. Every appointment is a window with a start and
 * an end, because a pre-task assessment is anchored to the moment work starts:
 * an appointment with no start time cannot be late, cannot be missed and cannot
 * be laid out against the hours either side of it. `appointments.is_all_day`
 * still exists - migrations are forward-only - and nothing reads or writes it.
 * An overnight job is entered as the window it actually is.
 *
 * ── State is `useState` and a JSON compare ─────────────────────────────────
 *
 * No react-hook-form, no zod resolver: the console does not use one anywhere,
 * and this form is not the place to introduce a second way of doing forms.
 */

/** The control the form opens on when it was reached from a row rather than Edit. */
type EditTarget = "assignee" | "time";

interface Draft {
  title: string;
  description: string;
  reference: string;
  customer_name: string;
  location_address: string;
  /**
   * Set only by picking a geocoder suggestion; cleared the moment the address
   * text is edited by hand, so the pair never describes text it no longer
   * matches. See `AddressField`.
   */
  location_lat: number | null;
  location_lng: number | null;
  date: string;
  start: string;
  end: string;
  assigned_to: string;
  priority: AppointmentPriority;
  notes: string;
}

const EMPTY: Draft = {
  title: "",
  description: "",
  reference: "",
  customer_name: "",
  location_address: "",
  location_lat: null,
  location_lng: null,
  date: "",
  start: "09:00",
  end: "10:00",
  assigned_to: "",
  priority: "normal",
  notes: "",
};

export function AppointmentPopover({
  open,
  anchor,
  point,
  siteId,
  companyId,
  timezone,
  zoneLabel,
  appointment,
  initial,
  canWrite,
  onClose,
  onSaved,
}: {
  open: boolean;
  /** The chip, hour cell, row or button the panel hangs off. */
  anchor: HTMLElement | null;
  /** Where in it the pointer was. See `CalendarPopover`. */
  point?: Point | null;
  siteId: string;
  /**
   * The company the console is working in. Appointment types are a company's
   * own list, and the endpoint is flat rather than nested under a site - so
   * without this an unbounded reader is offered every customer's types at once.
   */
  companyId: string | null;
  timezone: string;
  zoneLabel: string;
  /** Null when creating. */
  appointment: AppointmentWithContext | null;
  /** Prefill from a clicked slot: the day and the hour it started on. */
  initial?: { date: CalDate; hour: number } | null;
  canWrite: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const formId = React.useId();
  const toast = useToast();
  const confirm = useConfirm();

  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState<Draft>(EMPTY);
  const [baseline, setBaseline] = React.useState<Draft>(EMPTY);
  const [error, setError] = React.useState<string | null>(null);
  const [saving, setSaving] = React.useState(false);
  const [showMore, setShowMore] = React.useState(false);
  /** The cancellation panel, which asks for a reason rather than just confirming. */
  const [cancelling, setCancelling] = React.useState(false);
  /** Which control the form should open on, when the form was reached from a row. */
  const [focusField, setFocusField] = React.useState<EditTarget | null>(null);

  const people = useSiteUsers(open && editing ? siteId : null);

  // Rebuilt whenever the modal opens on a different record. Keyed on the
  // appointment and the prefill rather than on `open` alone, so reopening the
  // same row does not discard an edit in progress.
  React.useEffect(() => {
    if (!open) return;
    const next = appointment
      ? draftFrom(appointment, timezone)
      : {
          ...EMPTY,
          date: toValue(initial?.date ?? todayLike(timezone)),
          start: `${String(initial?.hour ?? 9).padStart(2, "0")}:00`,
          end: `${String(Math.min(23, (initial?.hour ?? 9) + 1)).padStart(2, "0")}:00`,
        };
    setDraft(next);
    setBaseline(next);
    setError(null);
    setEditing(!appointment);
    setFocusField(null);
    setCancelling(false);
    // Open on a record that already carries one of the folded fields, so
    // nothing this form can save is hidden behind a control the reader has no
    // reason to press.
    setShowMore(Boolean(next.reference || next.description || next.notes));
  }, [open, appointment, initial, timezone]);

  /**
   * Into the form, optionally landing on one control.
   *
   * "Assign" and "Reschedule" are the two things a dispatcher opens an
   * appointment to change, and both were "press Edit, then find the field" -
   * three fields down a twelve-field form for the one the reader had already
   * named by which row they clicked. The row says which; this puts the caret
   * there.
   */
  function startEditing(field: EditTarget | null = null) {
    setFocusField(field);
    setEditing(true);
  }

  // After the form has rendered, not before - the control does not exist until
  // `editing` has committed. Cleared straight away so pressing Back and Edit
  // again does not silently re-target a field nobody asked for.
  React.useEffect(() => {
    if (!editing || !focusField) return;
    document.getElementById(focusField === "assignee" ? "appt-assignee" : "appt-start")?.focus();
    setFocusField(null);
  }, [editing, focusField]);

  // Picking a type pre-fills the end time from its default duration, but only
  // while creating and only if the end has not been touched. Overwriting a time
  // somebody deliberately set is the kind of helpfulness that gets a form
  // sworn at.
  const window_ = React.useMemo(() => toWindow(draft, timezone), [draft, timezone]);

  const conflicts = useConflicts({
    enabled: open && editing && Boolean(draft.assigned_to) && Boolean(window_),
    siteId,
    assignedTo: draft.assigned_to,
    window: window_,
    excludeId: appointment?.id ?? null,
  });

  const dirty = editing && JSON.stringify(draft) !== JSON.stringify(baseline);
  const requestClose = useDiscardGuard(dirty, saving, onClose);

  /**
   * Back to the read view, which is a discard like any other.
   *
   * `useDiscardGuard` is wired to a close, so this asks on its own rather than
   * reusing it: leaving the form with unsaved edits loses them whether the
   * dialog goes away or only the form does.
   */
  async function requestView() {
    if (dirty && !saving) {
      const ok = await confirm({
        title: "Discard your changes?",
        message: "The edits you have made to this appointment will not be saved.",
        confirmLabel: "Discard",
        cancelLabel: "Keep editing",
        danger: true,
      });
      if (!ok) return;
    }
    setDraft(baseline);
    setError(null);
    setFocusField(null);
    setEditing(false);
  }

  const personOptions: FilterOption[] = [
    { value: "", label: "Unassigned" },
    ...(people.data?.users ?? []).map((person) => ({
      value: person.id,
      label: person.full_name ?? person.email,
      hint: person.email,
    })),
  ];

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    setError(null);

    if (!window_) {
      setError("Give the appointment a date, a start time and an end time.");
      return;
    }
    if (Date.parse(window_.end) <= Date.parse(window_.start)) {
      setError("The end time has to be after the start time.");
      return;
    }

    setSaving(true);
    try {
      const body = {
        title: draft.title.trim(),
        description: draft.description.trim() || null,
        reference: draft.reference.trim() || null,
        customer_name: draft.customer_name.trim() || null,
        location_address: draft.location_address.trim() || null,
        location_lat: draft.location_address.trim() ? draft.location_lat : null,
        location_lng: draft.location_address.trim() ? draft.location_lng : null,
        scheduled_start: window_.start,
        scheduled_end: window_.end,
        assigned_to: draft.assigned_to || null,
        priority: draft.priority,
        notes: draft.notes.trim() || null,
      };

      const result = appointment
        ? await api<{ conflicts?: unknown[] }>(`/appointments/${appointment.id}`, {
            method: "PATCH",
            body,
          })
        : await api<{ conflicts?: unknown[] }>(`/sites/${siteId}/appointments`, {
            method: "POST",
            body,
          });

      // The server's answer, not the form's guess. A clash outside the window
      // the calendar had loaded is only visible from here.
      const clashes = result.conflicts?.length ?? 0;
      toast.success(appointment ? "Appointment updated" : "Appointment scheduled", {
        description:
          clashes > 0 ? `${clashes} overlapping appointment on that person's day` : undefined,
      });
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not save that appointment"));
    } finally {
      setSaving(false);
    }
  }

  /**
   * Cancelling, and the reason that goes with it.
   *
   * `cancelAppointmentSchema` has carried `cancellation_reason` since it was
   * written and this console never sent one, so every row it cancelled answered
   * "why was this called off" with a null. The phone has asked for it from the
   * start. It stays **optional**, because a required reason produces "n/a" in a
   * column somebody will later try to report on - and it is asked for at all
   * because that is the one question a cancelled row exists to answer.
   */
  async function cancelAppointment() {
    if (!appointment) return;
    setCancelling(true);
  }

  async function submitCancellation(reason: string) {
    if (!appointment) return;

    setCancelling(false);
    setSaving(true);
    try {
      await api(`/appointments/${appointment.id}`, {
        method: "DELETE",
        body: { cancellation_reason: reason.trim() || null },
      });
      toast.success("Appointment cancelled");
      onSaved();
    } catch (err) {
      setError(apiErrorMessage(err, "Could not cancel that appointment"));
    } finally {
      setSaving(false);
    }
  }

  const isCancelled = appointment?.status === "cancelled";

  /**
   * Completed, and therefore not a plan any more.
   *
   * The work happened and a visit records it, so when it was booked for and who
   * it was on is evidence rather than a booking. Rescheduling, reassigning and
   * cancelling all describe work that has *not* happened yet, so the panel
   * offers none of them - and it withdraws the form behind them too. Hiding two
   * row links while leaving the same two fields editable one button away is a
   * rule that only looks like one.
   *
   * `status` rather than `visit`, because a visit can exist while the work is
   * still `in_progress` - underway is not done.
   */
  const isCompleted = appointment?.status === "completed";

  /** Can open the form at all. */
  const canEdit = canWrite && appointment !== null && !isCompleted;
  /** Can still change the plan: reschedule, reassign, call it off. */
  const canAmend = canEdit && !isCancelled;

  return (
    <CalendarPopover
      open={open}
      anchor={anchor}
      point={point}
      size={editing ? "md" : "sm"}
      onDismiss={() => {
        void requestClose();
        // `useDiscardGuard` closes on the spot unless there is something to
        // lose. When there is, it opens a question instead, and the panel needs
        // to know so the click that asked can be dropped rather than landing on
        // whatever is behind it.
        return dirty && !saving;
      }}
      title={appointment ? appointment.title : "New appointment"}
      /* Up beside the close, not in the footer. Edit does not change the record
         - it changes what this panel is showing - and putting it with the two
         that do gave a 368px panel a four-button bar that wrapped, landing
         "Cancel appointment" under the cursor. */
      action={
        !editing &&
        canEdit && (
          <Button variant="ghost" size="sm" icon={<Pencil />} onClick={() => startEditing()}>
            Edit
          </Button>
        )
      }
      /*
       * The footer is now only the two actions that end an appointment, and it
       * is absent entirely when neither applies - on a completed one, or for a
       * reader who cannot write. There is no Close button: the X is the close,
       * and Escape is the third way to do the same thing.
       *
       * Cancelling is a `ghost` in stop ink rather than a solid red block. It
       * was the loudest control in a panel opened to *read* an appointment,
       * sitting one mis-click from a booking somebody then has to reinstate;
       * quiet and last is the right weight for it. It still confirms.
       */
      footer={
        cancelling ? null : editing ? (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={() => (appointment ? void requestView() : void requestClose())}
            >
              {appointment ? "Back" : "Cancel"}
            </Button>
            <Button form={formId} type="submit" size="sm" loading={saving} disabled={!dirty}>
              {appointment ? "Save changes" : "Schedule it"}
            </Button>
          </>
        ) : canAmend ? (
          <>
            <div className="calpop__lead">
              <Button
                variant="ghost"
                size="sm"
                className="calpop__destroy"
                disabled={saving}
                onClick={() => void cancelAppointment()}
              >
                Cancel appointment
              </Button>
            </div>
          </>
        ) : null
      }
    >
      {cancelling && appointment ? (
        <CancelPanel
          title={appointment.title}
          saving={saving}
          onBack={() => setCancelling(false)}
          onConfirm={(reason) => void submitCancellation(reason)}
          error={error}
        />
      ) : !editing && appointment ? (
        <>
          <AppointmentDetails
            appointment={appointment}
            timezone={timezone}
            onEdit={canAmend ? startEditing : undefined}
          />
          <FormMessage>{error}</FormMessage>
        </>
      ) : (
        <Form id={formId} onSubmit={submit} className="appt-form">
          {/* Three questions, not twelve fields. The sections were four, each
              with a heading *and* a sentence under it restating the labels
              below - about 160px of a panel that has to fit beside the calendar
              it came from. What is left is a small label and a gap, which is
              enough to group four fields. */}
          <FormSection title="Job details">
            <Field label="Title" htmlFor="appt-title" required>
              <Input
                id="appt-title"
                autoFocus={!appointment}
                placeholder={PLACEHOLDER.appointmentTitle}
                value={draft.title}
                onChange={(e) => setDraft((d) => ({ ...d, title: e.target.value }))}
              />
            </Field>
          </FormSection>

          <FormSection title="Date & time">
            <div className="appt-when">
              <Field label="Date" htmlFor="appt-date" required>
                <DatePicker
                  id="appt-date"
                  label="Appointment date"
                  // The one control in the console that looks forward. Every other
                  // date filter is asking when something happened.
                  timeZone={timezone}
                  value={draft.date}
                  onChange={(value) => setDraft((d) => ({ ...d, date: value }))}
                  clearable={false}
                />
              </Field>

              {/* One question, two inputs. "Starts" and "Ends" were two labelled
                  fields on two rows and a third, read-only "Duration" row under
                  them - three rows of form for a single decision the reader
                  makes in one go. The duration is derived from the pair, so it
                  reads off the label rather than occupying a field of its own,
                  which is also where the eye already is when the end time
                  changes. */}
              <Field
                label="Time"
                htmlFor="appt-start"
                required
                labelAction={
                  window_ && (
                    <span className="appt-when__duration tnum">
                      {formatDuration(window_.start, window_.end)}
                    </span>
                  )
                }
              >
                <div className="appt-when__range">
                  {/* Native, deliberately. The console replaced `<select>` with
                      FilterSelect, but a time input is a different animal: it
                      gets 12/24-hour from the locale, it is keyboard and
                      screen-reader native, and a dropdown of 96 quarter-hours
                      would be a new primitive for no gain.

                      The visible label says "Time" for the pair, so each input
                      names itself - otherwise a screen reader announces the
                      second one as "Time" as well. */}
                  <Input
                    id="appt-start"
                    type="time"
                    aria-label="Start time"
                    value={draft.start}
                    onChange={(e) => setDraft((d) => ({ ...d, start: e.target.value }))}
                  />
                  <span className="appt-when__dash" aria-hidden>
                    -
                  </span>
                  <Input
                    id="appt-end"
                    type="time"
                    aria-label="End time"
                    value={draft.end}
                    onChange={(e) => setDraft((d) => ({ ...d, end: e.target.value }))}
                  />
                </div>
              </Field>
            </div>
          </FormSection>

          <FormSection title="Assignment">
            <div className="appt-assign">
              <Field label="Assigned to" htmlFor="appt-assignee">
                <FilterSelect
                  id="appt-assignee"
                  variant="field"
                  label="Assigned to"
                  placeholder="Unassigned"
                  searchable
                  value={draft.assigned_to}
                  options={personOptions}
                  onChange={(value) => setDraft((d) => ({ ...d, assigned_to: value }))}
                />
              </Field>

              <Field label="Priority" htmlFor="appt-priority">
                <FilterSelect
                  id="appt-priority"
                  variant="field"
                  label="Priority"
                  value={draft.priority}
                  options={APPOINTMENT_PRIORITIES.map((value) => ({
                    value,
                    label: PRIORITY_LABELS[value],
                  }))}
                  onChange={(value) =>
                    setDraft((d) => ({ ...d, priority: value as AppointmentPriority }))
                  }
                />
              </Field>
            </div>

            {/* Warned about, never blocked. Field work overruns and a supervisor
                moving a whole day pushes appointments through each other; a scheduler
                that refuses a double-booking is the same mistake as an app that
                blocks a technician. PRD §6: it warns, records and escalates. */}
            {conflicts.length > 0 && (
              <p className="conflict" role="status">
                <TriangleAlert aria-hidden />
                <span>
                  Already booked {conflicts.map((c) => `"${c.title}"`).join(", ")} in this window.
                  You can still save - this is a heads-up, not a block.
                </span>
              </p>
            )}
          </FormSection>

          <FormSection title="Location">
            <div className="form-grid">
              <Field label="Customer" htmlFor="appt-customer">
                <Input
                  id="appt-customer"
                  value={draft.customer_name}
                  placeholder={PLACEHOLDER.customer}
                  onChange={(e) => setDraft((d) => ({ ...d, customer_name: e.target.value }))}
                />
              </Field>

              <Field label="Address" htmlFor="appt-address">
                <AddressField
                  id="appt-address"
                  placeholder={PLACEHOLDER.address}
                  value={{
                    address: draft.location_address,
                    lat: draft.location_lat,
                    lng: draft.location_lng,
                  }}
                  onChange={(next) =>
                    setDraft((d) => ({
                      ...d,
                      location_address: next.address,
                      location_lat: next.lat,
                      location_lng: next.lng,
                    }))
                  }
                />
              </Field>
            </div>

            {/* Folded away, because these three are filled on a minority of
                appointments and were costing every one of them three rows.
                Native `<details>`, so the disclosure semantics, the keyboard and
                find-in-page all come from the browser.

                It opens itself when the record already carries any of them:
                collapsing a field somebody typed into hides data behind a
                control they have no reason to press, which is the one way this
                pattern is genuinely worse than three visible rows. */}
            <details
              className="appt-more"
              open={showMore}
              onToggle={(event) => setShowMore(event.currentTarget.open)}
            >
              <summary className="appt-more__summary">
                <ChevronRight className="appt-more__chevron" aria-hidden />
                Additional details
              </summary>

              <div className="appt-more__body form-grid">
                <Field label="Reference" htmlFor="appt-ref">
                  <Input
                    id="appt-ref"
                    placeholder={PLACEHOLDER.workOrder}
                    value={draft.reference}
                    onChange={(e) => setDraft((d) => ({ ...d, reference: e.target.value }))}
                  />
                </Field>

                <Field label="Description" htmlFor="appt-desc">
                  <Input
                    id="appt-desc"
                    placeholder={PLACEHOLDER.scopeOfWork}
                    value={draft.description}
                    onChange={(e) => setDraft((d) => ({ ...d, description: e.target.value }))}
                  />
                </Field>

                <Field label="Notes" htmlFor="appt-notes" className="form-grid__full">
                  <Input
                    id="appt-notes"
                    placeholder={PLACEHOLDER.notes}
                    value={draft.notes}
                    onChange={(e) => setDraft((d) => ({ ...d, notes: e.target.value }))}
                  />
                </Field>
              </div>
            </details>
          </FormSection>

          <FormMessage>{error}</FormMessage>
        </Form>
      )}
    </CalendarPopover>
  );
}

/* ───────────────────────────── cancelling ──────────────────────────── */

/**
 * Cancelling asks why, and the answer is optional.
 *
 * A panel rather than `useConfirm`, because a confirmation dialog is a yes/no
 * and this needs one field with it. The copy still carries the whole
 * consequence, which is the part a confirmation is actually for.
 *
 * Cancelled work comes off the calendar and stays in the table - the same
 * treatment `deleted_at` gets. "This was scheduled and did not happen" is still
 * answerable, by filtering for it; what it stopped doing is occupying a slot on
 * a schedule people read to find out what they are doing today.
 */
function CancelPanel({
  title,
  saving,
  onBack,
  onConfirm,
  error,
}: {
  title: string;
  saving: boolean;
  onBack: () => void;
  onConfirm: (reason: string) => void;
  error: string | null;
}) {
  const [reason, setReason] = React.useState("");

  return (
    <div className="appt-cancel">
      <p className="prose prose--sm">
        <strong>{title}</strong> comes off the schedule. The record is kept with your reason, and
        the Cancelled filter finds it again.
      </p>

      <Field
        label="Reason"
        htmlFor="appt-cancel-reason"
        // Optional, deliberately. A required reason produces "n/a" in a column
        // somebody will later try to report on.
        hint="Optional, and it is what a cancelled row exists to answer."
      >
        <Input
          id="appt-cancel-reason"
          autoFocus
          placeholder={PLACEHOLDER.cancelReason}
          value={reason}
          onChange={(event) => setReason(event.target.value)}
        />
      </Field>

      <FormMessage>{error}</FormMessage>

      <div className="appt-cancel__actions">
        <Button variant="secondary" size="sm" onClick={onBack} disabled={saving}>
          Keep it
        </Button>
        <Button variant="danger" size="sm" loading={saving} onClick={() => onConfirm(reason)}>
          Cancel appointment
        </Button>
      </div>
    </div>
  );
}

/* ────────────────────────────── the read view ───────────────────────────── */

/**
 * The appointment as a reader, not an editor, wants it.
 *
 * A `<dl>` rather than a stack of paragraphs: every row is a label and its
 * value, and that is the one structure a screen reader can announce as a pair.
 * The icons are decorative and the label carries the meaning, so nothing here
 * depends on recognising a pictogram.
 *
 * Empty fields are absent rather than dashed. Half of these are optional and a
 * list of six "-" is six lines the eye has to reject before finding the address.
 *
 * Two rows carry their own way into the form. Unassigned work is the thing a
 * dispatcher opened this screen to fix, and a time that has moved is the other;
 * both were "press Edit, then find the field", which is a form with eleven
 * irrelevant controls in it for a one-field change. `onEdit` is absent for a
 * reader who cannot write, and the rows go back to being text.
 */
function AppointmentDetails({
  appointment,
  timezone,
  onEdit,
}: {
  appointment: AppointmentWithContext;
  timezone: string;
  onEdit?: (field: EditTarget) => void;
}) {
  const day = parseValue(dayKeyIn(appointment.scheduled_start, timezone));
  const assignee = appointment.assignee?.full_name ?? appointment.assignee?.email ?? null;
  const overdue = isOverdue(appointment);

  return (
    <div className="appt-view">
      <div className="appt-view__strip">
        <span
          className={cn(
            "appt-view__swatch",
            overdue
              ? "appt-view__swatch--overdue"
              : `appt-view__swatch--prio-${appointment.priority}`
          )}
          aria-hidden
        />
        <StatusBadge status={appointment.status} overdue={overdue} />
        {appointment.priority !== "normal" && (
          <Badge tone={appointment.priority === "low" ? "neutral" : "caution"}>
            {PRIORITY_LABELS[appointment.priority]} priority
          </Badge>
        )}
      </div>

      <dl className="appt-view__rows">
        <DetailRow
          icon={<Clock />}
          label="When"
          action={
            onEdit && (
              <RowAction icon={<CalendarClock />} onClick={() => onEdit("time")}>
                Reschedule
              </RowAction>
            )
          }
        >
          <span>{day ? formatFullDay(toLocalDate(day)) : "-"}</span>
          <span className="appt-view__times tnum">
            {`${formatTimeIn(appointment.scheduled_start, timezone)} - ` +
              `${formatTimeIn(appointment.scheduled_end, timezone)} · ` +
              formatDuration(appointment.scheduled_start, appointment.scheduled_end)}
          </span>
        </DetailRow>

        <DetailRow
          icon={<CircleUser />}
          label="Assigned to"
          action={
            onEdit && (
              <RowAction icon={<UserPlus />} onClick={() => onEdit("assignee")}>
                {assignee ? "Reassign" : "Assign"}
              </RowAction>
            )
          }
        >
          {assignee ?? <span className="appt-view__none">Unassigned</span>}
        </DetailRow>

        {appointment.location_address && (
          <DetailRow icon={<MapPin />} label="Where">
            {appointment.location_address}
          </DetailRow>
        )}

        {appointment.customer_name && (
          <DetailRow icon={<Building2 />} label="Customer">
            {appointment.customer_name}
          </DetailRow>
        )}

        {appointment.reference && (
          <DetailRow icon={<Hash />} label="Work order reference">
            {appointment.reference}
          </DetailRow>
        )}

        {appointment.description && (
          <DetailRow icon={<AlignLeft />} label="Description">
            {appointment.description}
          </DetailRow>
        )}

        {appointment.notes && (
          <DetailRow icon={<StickyNote />} label="Notes">
            {appointment.notes}
          </DetailRow>
        )}

        {appointment.status === "cancelled" && (
          <DetailRow icon={<CircleSlash />} label="Cancelled">
            {appointment.cancellation_reason || (
              <span className="appt-view__none">No reason recorded</span>
            )}
          </DetailRow>
        )}

        {appointment.visit && (
          <DetailRow icon={<ClipboardCheck />} label="Visit">
            <TextLink to={`/app/visits/${appointment.visit.id}`}>Open the visit</TextLink>
          </DetailRow>
        )}
      </dl>
    </div>
  );
}

function DetailRow({
  icon,
  label,
  action,
  children,
}: {
  icon: React.ReactNode;
  label: string;
  /** Sits on the label's line. Falsy for a row that is only a value. */
  action?: React.ReactNode;
  children: React.ReactNode;
}) {
  return (
    <div className={cn("appt-row", Boolean(action) && "appt-row--actionable")}>
      <span className="appt-row__icon" aria-hidden>
        {icon}
      </span>
      <dt className="appt-row__label">{label}</dt>
      {action}
      <dd className="appt-row__value">{children}</dd>
    </div>
  );
}

/**
 * A row's own way into the form.
 *
 * Not a `Button`. The console's smallest button is 32px and carries a border
 * and a background; three of those down a read panel turn a list of facts into
 * a toolbar. This is the label-sized text control the row's own line has room
 * for, and it is a real `<button>` so the keyboard and the accessible name come
 * from the browser.
 */
function RowAction({
  icon,
  onClick,
  children,
}: {
  icon: React.ReactNode;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button type="button" className="appt-row__action" onClick={onClick}>
      <span aria-hidden>{icon}</span>
      {children}
    </button>
  );
}

/* ────────────────────────────── helpers ────────────────────────────── */

function todayLike(tz: string): CalDate {
  const parts = zonedParts(new Date().toISOString(), tz);
  return { y: parts.y, m: parts.m, d: parts.d };
}

function draftFrom(appointment: AppointmentWithContext, tz: string): Draft {
  const start = zonedParts(appointment.scheduled_start, tz);
  return {
    title: appointment.title,
    description: appointment.description ?? "",
    reference: appointment.reference ?? "",
    customer_name: appointment.customer_name ?? "",
    location_address: appointment.location_address ?? "",
    location_lat: appointment.location_lat,
    location_lng: appointment.location_lng,
    date: toValue(start),
    start: timeInputValue(appointment.scheduled_start, tz),
    end: timeInputValue(appointment.scheduled_end, tz),
    assigned_to: appointment.assigned_to ?? "",
    priority: appointment.priority,
    notes: appointment.notes ?? "",
  };
}

/** The draft's wall-clock fields as the UTC instants the API stores. */
function toWindow(draft: Draft, tz: string): { start: string; end: string } | null {
  const date = parseValue(draft.date);
  if (!date) return null;

  const start = parseTimeInput(draft.start);
  const end = parseTimeInput(draft.end);
  if (!start || !end) return null;

  return {
    start: instantFrom(date, start.hh, start.mm, tz),
    end: instantFrom(date, end.hh, end.mm, tz),
  };
}

interface Clash {
  id: string;
  title: string;
}

/**
 * Overlapping work for the same person, checked while the form is open.
 *
 * The server answers authoritatively on save; this exists so the warning appears
 * *before* somebody commits, which is the only point at which it can change what
 * they do. Debounced because it fires on every keystroke in the time fields.
 */
function useConflicts(args: {
  enabled: boolean;
  siteId: string;
  assignedTo: string;
  window: { start: string; end: string } | null;
  excludeId: string | null;
}): Clash[] {
  const [debounced, setDebounced] = React.useState(args.window);

  React.useEffect(() => {
    const id = window.setTimeout(() => setDebounced(args.window), 300);
    return () => window.clearTimeout(id);
  }, [args.window?.start, args.window?.end]); // eslint-disable-line react-hooks/exhaustive-deps

  const key =
    args.enabled && debounced && args.assignedTo
      ? `/sites/${args.siteId}/appointments?assigned_to=${args.assignedTo}` +
        `&from=${encodeURIComponent(debounced.start)}&to=${encodeURIComponent(debounced.end)}` +
        `&pageSize=20`
      : null;

  const { data } = useApi<{ appointments: AppointmentWithContext[] }>(key);

  return (data?.appointments ?? [])
    .filter((row) => row.id !== args.excludeId && row.status !== "cancelled")
    .map((row) => ({ id: row.id, title: row.title }));
}
